import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const nav = [
  { to: "/", label: "Home" },
  { to: "/gallery", label: "Gallery" },
  { to: "/products", label: "Products" },
  { to: "/design-studio", label: "Design Studio" },
  { to: "/custom-order", label: "Custom Order" },
  { to: "/bulk", label: "Bulk & Business" },
  { to: "/about", label: "About" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = React.useState(false);
  const { user, logout } = useAuth();
  const nav_go = useNavigate();

  return (
    <header data-testid="site-header" className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b hairline">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 shrink-0 mr-6">
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_ezjem-engrave/artifacts/jum9qgk9_COMPANY%20LOGO.webp"
            alt="EZ JEM Designs"
            className="h-12 w-12 rounded-full object-cover"
          />
          <span className="hidden sm:inline xl:hidden 2xl:inline whitespace-nowrap font-serif text-2xl tracking-tight leading-none" style={{color: "var(--primary)"}}>EZ JEM<span style={{color: "var(--accent-metal)"}}> Designs</span></span>
        </Link>
        <nav className="hidden xl:flex items-center gap-4 2xl:gap-6">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide whitespace-nowrap transition-colors ${isActive ? "text-[color:var(--primary)]" : "text-[color:var(--text-muted)] hover:text-[color:var(--primary)]"}`
              }>{n.label}</NavLink>
          ))}
        </nav>
        <div className="hidden xl:flex items-center gap-3 ml-4 shrink-0">
          {user ? (
            <>
              {user.is_admin && (
                <DropdownMenu>
                  <DropdownMenuTrigger data-testid="header-admin-menu" className="flex items-center gap-1 text-sm font-medium whitespace-nowrap outline-none" style={{color: "var(--accent-metal)"}}>
                    Admin <ChevronDown size={14}/>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild><Link to="/admin/orders" data-testid="header-admin-orders">Orders</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/admin/inbox" data-testid="header-admin-inbox">Inbox</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/admin/reviews" data-testid="header-admin-reviews">Reviews</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/admin/discount-codes" data-testid="header-admin-codes">Discount codes</Link></DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Link to="/dashboard" data-testid="header-dashboard" className="flex items-center gap-2 text-sm font-medium">
                <User size={16}/> <span className="max-w-[90px] truncate">{user.name?.split(" ")[0] || "Account"}</span>
              </Link>
              <Button data-testid="header-logout" onClick={() => { logout(); nav_go("/"); }} variant="ghost" size="sm"><LogOut size={16} className="2xl:mr-1"/><span className="hidden 2xl:inline">Sign out</span></Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login" className="text-sm font-medium text-[color:var(--text-muted)] hover:text-[color:var(--primary)]">Sign in</Link>
              <Link to="/register" data-testid="header-register"><Button className="btn-metal rounded-full px-5">Start Creating</Button></Link>
            </>
          )}
        </div>
        <button data-testid="mobile-menu-btn" className="xl:hidden" onClick={() => setOpen(!open)} aria-label="menu">
          {open ? <X/> : <Menu/>}
        </button>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="xl:hidden border-t hairline bg-white">
          <div className="px-6 py-4 flex flex-col gap-3">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-sm font-medium text-[color:var(--primary)]">{n.label}</Link>
            ))}
            {user ? (
              <>
                {user.is_admin && (
                  <>
                    <Link to="/admin/orders" onClick={() => setOpen(false)} className="text-sm font-medium" style={{color: "var(--accent-metal)"}}>Admin · Orders</Link>
                    <Link to="/admin/inbox" onClick={() => setOpen(false)} className="text-sm font-medium" style={{color: "var(--accent-metal)"}}>Admin · Inbox</Link>
                    <Link to="/admin/reviews" onClick={() => setOpen(false)} className="text-sm font-medium" style={{color: "var(--accent-metal)"}}>Admin · Reviews</Link>
                  </>
                )}
                <Link to="/dashboard" onClick={() => setOpen(false)} className="text-sm font-medium">Dashboard</Link>
                <button onClick={() => { logout(); setOpen(false); }} className="text-left text-sm font-medium">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-medium">Sign in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="text-sm font-medium">Start creating</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

const SOCIALS = [
  { key: "facebook", label: "Facebook", href: "https://www.facebook.com/share/17ZDZisS57/?mibextid=wwXIfr",
    path: "M14 8.5V6.6c0-.8.2-1.3 1.4-1.3H17V2.2c-.3 0-1.3-.2-2.5-.2-2.5 0-4.1 1.5-4.1 4.2v2.3H7.7v3.2h2.7V22H14v-10.3h2.7l.4-3.2H14z" },
  { key: "instagram", label: "Instagram", href: "https://www.instagram.com/ezjemcreations",
    path: "M12 2.2c3.2 0 3.6 0 4.8.1 3.3.1 4.8 1.7 4.9 4.9.1 1.3.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 3.2-1.7 4.8-4.9 4.9-1.3.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-3.3-.1-4.8-1.7-4.9-4.9C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8C2.4 3.9 3.9 2.4 7.2 2.3 8.4 2.2 8.8 2.2 12 2.2zm0 4.9a4.9 4.9 0 100 9.8 4.9 4.9 0 000-9.8zm0 8.1a3.2 3.2 0 110-6.4 3.2 3.2 0 010 6.4zm5.1-9.4a1.2 1.2 0 100 2.3 1.2 1.2 0 000-2.3z" },
  { key: "tiktok", label: "TikTok", href: "https://www.tiktok.com/@ezjemcreations",
    path: "M16.6 5.8A4.3 4.3 0 0115.5 3h-3.1v12.4a2.6 2.6 0 11-2.6-2.6c.3 0 .5 0 .8.1V9.7a5.8 5.8 0 00-.8-.1 5.8 5.8 0 105.8 5.8V9.1a7.3 7.3 0 004.3 1.4V7.4a4.3 4.3 0 01-3.3-1.6z" },
];

export function SocialLinks({ className = "", dark = false }) {
  return (
    <div className={`flex gap-3 ${className}`} data-testid="social-links">
      {SOCIALS.map((s) => (
        <a key={s.key} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} data-testid={`social-${s.key}`}
          className={`h-10 w-10 rounded-full flex items-center justify-center border transition-colors ${dark ? "border-[color:var(--primary)]/20 text-[color:var(--primary)] hover:bg-[color:var(--primary)] hover:text-white" : "border-white/20 hover:bg-white hover:text-[color:var(--primary)]"}`}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d={s.path}/></svg>
        </a>
      ))}
    </div>
  );
}

export function Footer() {
  return (
    <footer data-testid="site-footer" className="mt-24 relative" style={{background: "var(--primary)", color: "#EFEDE8"}}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-10 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-4">
            <img src="https://customer-assets-4nw71qhi.emergentagent.net/job_ezjem-engrave/artifacts/jum9qgk9_COMPANY%20LOGO.webp" alt="EZ JEM Designs" className="h-14 w-14 rounded-full object-cover"/>
            <div className="font-serif text-4xl tracking-tight leading-none">EZ JEM <span style={{color: "var(--accent-metal)"}}>Designs</span></div>
          </div>
          <p className="mt-6 max-w-md text-sm opacity-80 leading-relaxed">Precision engraving for the moments that matter — births, memorials, awards, brands and art. Hand-finished on wood, metal, stone and glass.</p>
          <div className="metal-line mt-8 max-w-xs"/>
          <SocialLinks className="mt-6"/>
        </div>
        <div>
          <div className="uppercase text-xs tracking-[0.2em] opacity-60 mb-4">Explore</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/gallery" className="hover:text-white">Idea Library</Link></li>
            <li><Link to="/products" className="hover:text-white">Products</Link></li>
            <li><Link to="/design-studio" className="hover:text-white">Design Studio</Link></li>
            <li><Link to="/bulk" className="hover:text-white">Bulk & Business</Link></li>
          </ul>
        </div>
        <div>
          <div className="uppercase text-xs tracking-[0.2em] opacity-60 mb-4">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-white">About</Link></li>
            <li><Link to="/reviews" className="hover:text-white">Reviews</Link></li>
            <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs opacity-60">© {new Date().getFullYear()} EZJEM CREATIONS. Etched with care.</div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="App">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
